import React, { FC, useState } from "react"

import { useTranslation } from "react-i18next"

import { SaveButton } from "components/buttons/SaveButton"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { InputField } from "components/inputs/InputField"
import { Modal } from "components/modals/Modal"
import { Toggle } from "components/toggles/Toggle"

import { postbacksService } from "../../services/postbacks/postbacks.service"
import {
  PLACEHOLDER_CHEAT_SHEET,
  Postback,
  POSTBACK_EVENTS,
  POSTBACK_FILTER_MODES,
  POSTBACK_METHODS,
  PostbackFilter,
  PostbackFilterField,
  PostbackInput
} from "../../services/postbacks/type"

interface Props {
  initial?: Postback | null
  onClose: () => void
  onSaved: () => void
}

export const PostbackEditorModal: FC<Props> = ({ initial, onClose, onSaved }) => {
  const { t } = useTranslation(["translation"], { useSuspense: false })
  const [form, setForm] = useState<PostbackInput>({
    name: initial?.name ?? "",
    enabled: initial?.enabled ?? true,
    event: initial?.event ?? "registration",
    method: initial?.method ?? "GET",
    url_template: initial?.url_template ?? "",
    filter_mode: initial?.filter_mode ?? "ALL",
    filters: initial?.filters ?? []
  })
  const [saving, setSaving] = useState(false)

  const set = <K extends keyof PostbackInput>(key: K, value: PostbackInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const updateFilter = (index: number, patch: Partial<PostbackFilter>) =>
    set(
      "filters",
      form.filters.map((f, i) => (i === index ? { ...f, ...patch } : f))
    )

  const addFilter = () => set("filters", [...form.filters, { field: "referral_code", operator: "equals", value: "" }])

  const removeFilter = (index: number) => set("filters", form.filters.filter((_, i) => i !== index))

  const save = async () => {
    setSaving(true)
    const payload: PostbackInput = {
      ...form,
      name: form.name.trim(),
      url_template: form.url_template.trim(),
      filters: form.filters.filter((f) => f.value.trim() !== "")
    }
    const result = initial
      ? await postbacksService.update(initial.id, payload)
      : await postbacksService.create(payload)
    setSaving(false)
    if (result) onSaved()
  }

  const isHttp = form.url_template.trim().toLowerCase().startsWith("http://")

  const eventOptions = POSTBACK_EVENTS.map((ev) => ({ name: ev, value: ev }))
  const methodOptions = POSTBACK_METHODS.map((m) => ({ name: m, value: m }))
  const filterModeOptions = POSTBACK_FILTER_MODES.map((m) => ({ name: t("s2sPostbacks.editor.matchMode", { mode: m }), value: m }))

  return (
    <Modal open handler={onClose}>
      <div className="modal-box max-h-[90vh] w-full max-w-2xl overflow-y-auto">
        <h2 className="mb-4 text-lg font-semibold">
          {initial ? t("s2sPostbacks.editor.titleEdit") : t("s2sPostbacks.editor.titleNew")}
        </h2>

        <InputField
          title={t("s2sPostbacks.editor.name")}
          value={form.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("name", e.target.value)}
          placeholder={t("s2sPostbacks.editor.namePlaceholder")}
        />

        <div className="grid grid-cols-3 items-end gap-4">
          <DropdownMenu
            title={t("s2sPostbacks.editor.event")}
            options={eventOptions}
            value={form.event}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => set("event", e.target.value as PostbackInput["event"])}
            hideEmptyOption
            disableEmptyOption
            noAllValueText
          />
          <DropdownMenu
            title={t("s2sPostbacks.editor.method")}
            options={methodOptions}
            value={form.method}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => set("method", e.target.value as PostbackInput["method"])}
            hideEmptyOption
            disableEmptyOption
            noAllValueText
          />
          <div className="form-control">
            <label className="label">
              <span className="label-text">{t("s2sPostbacks.editor.enabled")}</span>
            </label>
            <div className="flex h-12 items-center gap-2">
              <Toggle value={form.enabled} handler={(v: boolean) => set("enabled", v)} />
              <span className="text-sm">{form.enabled ? t("s2sPostbacks.editor.on") : t("s2sPostbacks.editor.off")}</span>
            </div>
          </div>
        </div>

        <div className="form-control mt-2">
          <label className="label">
            <span className="label-text">{t("s2sPostbacks.editor.urlTemplate")}</span>
          </label>
          <textarea
            className="textarea textarea-bordered h-24 w-full bg-[rgba(0,0,0,0.05)] font-mono text-sm dark:bg-[rgba(0,0,0,0.2)]"
            value={form.url_template}
            onChange={(e) => set("url_template", e.target.value)}
            placeholder="https://track.example.com/click?cnv_id={subid}&cnv_status=registration"
          />
        </div>
        {isHttp && <p className="mb-2 text-xs text-amber-500">{t("s2sPostbacks.editor.httpWarning")}</p>}

        <div className="my-4 rounded-lg bg-base-300 p-3">
          <p className="mb-1 text-xs font-medium opacity-60">{t("s2sPostbacks.editor.placeholders")}</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {PLACEHOLDER_CHEAT_SHEET.map((p) => (
              <div key={p.token} className="text-xs opacity-70">
                <code className="text-purple">{p.token}</code> — {t(`s2sPostbacks.editor.ph.${p.key}`)}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-2 flex items-center justify-between gap-4">
          <label className="label-text">{t("s2sPostbacks.editor.filtersLabel")}</label>
          <div className="w-40">
            <DropdownMenu
              options={filterModeOptions}
              value={form.filter_mode}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                set("filter_mode", e.target.value as PostbackInput["filter_mode"])
              }
              hideEmptyOption
              disableEmptyOption
              noAllValueText
            />
          </div>
        </div>

        {form.filters.map((f, i) => (
          <div key={i} className="mb-2 flex items-center gap-2">
            <select
              className="select select-bordered select-sm"
              value={f.field}
              onChange={(e) => updateFilter(i, { field: e.target.value as PostbackFilterField })}
            >
              <option value="referral_code">referral_code</option>
              <option value="affiliate_username">affiliate_username</option>
            </select>
            <span className="text-xs opacity-50">{t("s2sPostbacks.editor.equals")}</span>
            <input
              className="input input-bordered input-sm flex-1 bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)]"
              value={f.value}
              onChange={(e) => updateFilter(i, { value: e.target.value })}
              placeholder={t("s2sPostbacks.editor.value")}
            />
            <button className="btn btn-ghost btn-sm text-rose-400" onClick={() => removeFilter(i)} type="button">
              ✕
            </button>
          </div>
        ))}
        <button className="btn btn-ghost btn-sm" onClick={addFilter} type="button">
          {t("s2sPostbacks.editor.addFilter")}
        </button>
        {form.filters.length === 0 && <p className="mt-1 text-xs opacity-50">{t("s2sPostbacks.editor.filtersEmpty")}</p>}

        <div className="modal-action">
          <button className="btn btn-ghost" onClick={onClose} type="button">
            {t("s2sPostbacks.editor.cancel")}
          </button>
          <SaveButton onClick={save} loading={saving} disabled={!form.name.trim() || !form.url_template.trim()}>
            {t("s2sPostbacks.editor.save")}
          </SaveButton>
        </div>
      </div>
    </Modal>
  )
}
